import type { RentalManagerAuthCheckUserInfoWebApiDto } from '@zg-rentals/ts-rental-manager-api-client';
import { ErrorPage } from '@zg-rentals/visual-cohesion';
import { getGlobalMonitor } from '@zg-rentals/monitor-browser';
import { logError } from '@zg-rentals/log-error';
import type { ReactNode } from 'react';
import { RentalManagerUserInfoV2WebApiHandler_get_queryOptions } from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { client } from './rentalManagerClient';
import { createAuthPredicates } from '@zg-rentals/auth-comet/mfa';
import type { RedirectHref } from '@zg-rentals/auth-react/legacy';
import { defineAuthProvider } from '@zg-rentals/auth-react/legacy';
import { rentalManagerLoginOptions, rentalManagerStepUpLoginOptions } from '@zg-rentals/login-zillow';
import { zillowLoginRedirectHref } from '@zg-rentals/login-zillow/client';
import { Instrument } from '@zg-rentals/react-use';

export type User = RentalManagerAuthCheckUserInfoWebApiDto;

const APP_NAME = 'payments-overview-web';
const BUILD_NUMBER = process.env.NEXT_PUBLIC_BUILD_NUMBER;

// Returns all values for a cookie name joined by ',' — same-name cookies can
// coexist when scoped to different Domain/Path, and surfacing all of them is
// the point of this logging. Instrument's onInitialRender runs during render
// (not in an effect), so this can execute during SSR where `window`/`document`
// are undefined.
function getCookie(name: string) {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  const matches = document.cookie
    .split('; ')
    .filter((c) => c.startsWith(name + '='))
    .map((c) => c.slice(name.length + 1));

  return matches.length ? matches.join(',') : undefined;
}

const UnauthorizedFallback = ({ errorType, message }: { errorType: string; message: string }) => (
  <Instrument
    onInitialRender={() =>
      logError({
        error: new Error(message),
        errorType,
        context: {
          appName: APP_NAME,
          buildNumber: BUILD_NUMBER,
          zgcus_aeut: getCookie('zgcus_aeut'),
          zgcus_aeuut: getCookie('zgcus_aeuut'),
          zg_anonymous_id: getCookie('zg_anonymous_id'),
          zguid: getCookie('zguid'),
        },
      })
    }
  >
    <ErrorPage
      errorDetails="Please try again later."
      action={<a href="/rental-manager/">Return to Zillow Rental Manager</a>}
    />
  </Instrument>
);

const { hasInitiatedAuth, hasAuth } = createAuthPredicates((user: User) => ({ ...user, isLoggedIn: user.loggedIn }));

export const Auth = defineAuthProvider({
  queryOptions: () => RentalManagerUserInfoV2WebApiHandler_get_queryOptions({}, { httpClient: client }),
  isAuthorized: hasInitiatedAuth,
});

export const { useAuth } = Auth;

const loginRedirectHref = zillowLoginRedirectHref({
  baseURL: process.env.NEXT_PUBLIC_AUTH_BASE_URL,
  entryPoint: APP_NAME,
  forwardPrefix: process.env.NEXT_PUBLIC_AUTH_FORWARD_PREFIX,
  ...rentalManagerLoginOptions,
});

const mfaStepUpRedirectHref = zillowLoginRedirectHref({
  baseURL: process.env.NEXT_PUBLIC_AUTH_BASE_URL,
  entryPoint: APP_NAME,
  forwardPrefix: process.env.NEXT_PUBLIC_AUTH_FORWARD_PREFIX,
  ...rentalManagerLoginOptions,
  ...rentalManagerStepUpLoginOptions,
});

const loading = <>Loading…</>;
const redirecting = <>Redirecting…</>;

const mfaRedirectHref: RedirectHref = (href, params) => {
  getGlobalMonitor()?.count(
    { name: 'mfa step-up attempt total' },
    { name: 'mfa step-up attempt type MfaStepUpAttempt' },
  );

  return mfaStepUpRedirectHref(href, params);
};

const onMfaRedirectAuthorized = () =>
  getGlobalMonitor()?.count(
    { name: 'mfa step-up success total' },
    { name: 'mfa step-up success type MfaStepUpSuccess' },
  );

/**
 * Takes care of initial redirect to login, then optional MFA step-up if requested by backend
 */
export const AppAuthBoundary = ({ children }: { children: (user: User) => ReactNode }) => {
  return (
    <Auth.Boundary
      loading={loading}
      redirecting={redirecting}
      redirectHref={loginRedirectHref}
      onUnauthorized={
        <UnauthorizedFallback errorType="LoginUnauthorized" message="User unauthorized after login redirect" />
      }
    >
      <Auth.Boundary
        loading={loading}
        redirecting={redirecting}
        isAuthorized={hasAuth}
        redirectHref={mfaRedirectHref}
        onRedirectAuthorized={onMfaRedirectAuthorized}
        searchParam={['sp', 'x']}
        onUnauthorized={
          <UnauthorizedFallback errorType="MfaUnauthorized" message="User unauthorized after MFA step-up redirect" />
        }
      >
        {children}
      </Auth.Boundary>
    </Auth.Boundary>
  );
};
